import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Collapse, List, message, Modal, Radio, Select, Space, Spin, Tag, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { CloseCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  analyzeOrderRestockOptions,
  canRestockProductToCrate,
  executeRestockPlan,
  groupByPackageDate,
  sanitizeCrateDocId,
} from "../../../../../functions/crateStock";
import { useProducts } from "../../../../../data/hooks/firestore/useProducts";
import { useAuth } from "../../../../../data/hooks/firebase/useAuth";

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;
const { Option } = Select;
const CRATE_BARCODE_LENGTH = 99;

/**
 * Modal for unpacking orders with crate stock restocking options
 * Shows analysis of original crates and allows user to choose restocking strategy
 */
const UnpackOrderModal = ({ open, onCancel, onConfirm, order, crateStocks = [], loading = false }) => {
  const { t } = useTranslation();
  const { products, getProductById } = useProducts();
  const { email, uid } = useAuth();

  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [restockMode, setRestockMode] = useState("fixed"); // "original", "fixed", or "skip" - default to fixed
  const [restockPlan, setRestockPlan] = useState({}); // User's chosen crate assignments
  const [submitting, setSubmitting] = useState(false);
  const [scanBuffer, setScanBuffer] = useState("");
  const [scanTimer, setScanTimer] = useState(null);
  const [globalFixedCrate, setGlobalFixedCrate] = useState(null); // Single crate for all products in fixed mode

  // Available empty crates for new assignments (regular crates only)
  const availableEmptyCrates = useMemo(() => {
    return (crateStocks || []).filter((c) => {
      const itemsCount = typeof c.itemsCount === "number" ? c.itemsCount : 0;
      const isFixed = c.crateType === "fixed_dumping" || c.isFixedDumpingCrate === true;
      return c.status === "available" && itemsCount === 0 && c.status !== "lost" && !isFixed;
    });
  }, [crateStocks]);

  // Fixed dumping crates for cancelled order restocking
  const fixedDumpingCrates = useMemo(() => {
    return (crateStocks || []).filter((c) => {
      const isFixed = c.crateType === "fixed_dumping" || c.isFixedDumpingCrate === true;
      return isFixed && c.status !== "lost";
    });
  }, [crateStocks]);

  // Analyze order when modal opens
  useEffect(() => {
    if (!open || !order) {
      setAnalysis(null);
      setRestockMode("fixed");
      setRestockPlan({});
      setGlobalFixedCrate(null);
      return;
    }

    const performAnalysis = async () => {
      setAnalyzing(true);
      try {
        const result = await analyzeOrderRestockOptions(order, crateStocks);
        setAnalysis(result);

        // Initialize restock plan
        const initialPlan = {};
        for (const product of result.products) {
          initialPlan[product.productId] = product.crateLines.map((line) => ({
            originalCrateDocId: line.crateDocId,
            targetCrateDocId: line.canRestockToOriginal ? line.crateDocId : null,
            quantity: line.quantity,
            packageDateUnix: line.packageDateUnix,
            isNewCrate: !line.canRestockToOriginal,
            subItemProductId: line.subItemProductId || null,
          }));
        }
        setRestockPlan(initialPlan);

        // Default to fixed mode (dump to fixed crates)
        // User can switch to original mode if all crates are available
        setRestockMode("fixed");
      } catch (error) {
        console.error("Error analyzing restock options:", error);
        message.error(t("unpack_order.error_analyzing"));
      } finally {
        setAnalyzing(false);
      }
    };

    performAnalysis();
  }, [open, order, crateStocks, t]);

  // Handle barcode scanning for new crate assignment
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e) => {
      if (!open) return;

      if (/^[0-9]$/.test(e.key)) {
        const newBuffer = `${scanBuffer}${e.key}`.slice(-CRATE_BARCODE_LENGTH);
        setScanBuffer(newBuffer);

        if (scanTimer) clearTimeout(scanTimer);
        const timer = setTimeout(() => {
          setScanBuffer("");
        }, 120);
        setScanTimer(timer);
        return;
      }

      if (e.key === "Enter" && scanBuffer) {
        const code = scanBuffer;
        setScanBuffer("");
        handleCrateScan(code);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      if (scanTimer) clearTimeout(scanTimer);
    };
  }, [open, scanBuffer, scanTimer, restockPlan]);

  const handleCrateScan = async (barcode) => {
    const trimmed = String(barcode || "").trim();
    if (!trimmed || trimmed.length > CRATE_BARCODE_LENGTH) {
      message.error(t("crate_stock.invalid_crate_barcode"));
      return;
    }

    // Check if this is an available crate
    const crateDocId = sanitizeCrateDocId(trimmed);
    const existingCrate = crateStocks.find((c) => String(c.crateId).trim() === trimmed || c.id === crateDocId);

    if (existingCrate) {
      const itemsCount = typeof existingCrate.itemsCount === "number" ? existingCrate.itemsCount : 0;
      if (existingCrate.status === "lost") {
        message.error(t("unpack_order.crate_marked_lost"));
        return;
      }
      if (itemsCount > 0) {
        message.error(t("unpack_order.crate_not_empty"));
        return;
      }
    }

    // In fixed mode, assign this crate globally to all products
    if (restockMode === "fixed") {
      handleGlobalFixedCrateSelect(crateDocId);
      message.success(t("unpack_order.global_crate_assigned", { crateId: trimmed }));
      return;
    }

    // In other modes, find first unassigned item and assign it
    let assigned = false;
    const newPlan = { ...restockPlan };

    for (const [productId, lines] of Object.entries(newPlan)) {
      for (const line of lines) {
        if (!line.targetCrateDocId && !assigned) {
          line.targetCrateDocId = crateDocId;
          line.isNewCrate = true;
          assigned = true;

          // Check if this crate can receive this product
          canRestockProductToCrate(crateDocId, productId, line.packageDateUnix).then((result) => {
            if (!result.canRestock || !result.productCompatible || !result.packageDateMatch) {
              message.warning(t("unpack_order.crate_product_date_mismatch"));
            }
          });

          break;
        }
      }
    }

    if (assigned) {
      setRestockPlan(newPlan);
      message.success(t("unpack_order.crate_assigned", { crateId: trimmed }));
    } else {
      message.info(t("unpack_order.no_unassigned_items"));
    }
  };

  const handleManualCrateSelect = (productId, lineIndex, crateDocId) => {
    const newPlan = { ...restockPlan };
    const lines = [...newPlan[productId]];
    lines[lineIndex] = {
      ...lines[lineIndex],
      targetCrateDocId: crateDocId,
      isNewCrate: lines[lineIndex].originalCrateDocId !== crateDocId,
      subItemProductId: lines[lineIndex].subItemProductId || null,
    };
    newPlan[productId] = lines;
    setRestockPlan(newPlan);
  };

  const handleGlobalFixedCrateSelect = (crateDocId) => {
    setGlobalFixedCrate(crateDocId);

    // Assign this crate to all products
    const newPlan = {};
    for (const product of analysis?.products || []) {
      newPlan[product.productId] = product.crateLines.map((line) => ({
        originalCrateDocId: line.crateDocId,
        targetCrateDocId: crateDocId,
        quantity: line.quantity,
        packageDateUnix: line.packageDateUnix,
        isNewCrate: true,
        isFixedCrate: true,
        subItemProductId: line.subItemProductId || null,
      }));
    }
    setRestockPlan(newPlan);
  };

  const getProductName = (productId) => {
    const product =
      getProductById?.(productId) || products.find((p) => p.id === productId || p.productId === productId);
    return product?.productName || product?.name?.en || productId;
  };

  const isPlanComplete = useMemo(() => {
    // Skip mode doesn't need plan completion
    if (restockMode === "skip") return true;

    if (!analysis || !restockPlan) return false;

    for (const product of analysis.products) {
      const lines = restockPlan[product.productId] || [];
      for (const line of lines) {
        if (!line.targetCrateDocId) return false;
      }
    }
    return true;
  }, [analysis, restockPlan, restockMode]);

  const getUnassignedItems = useMemo(() => {
    if (!analysis || !restockPlan) return [];

    const unassigned = [];
    for (const product of analysis.products) {
      const lines = restockPlan[product.productId] || [];
      for (const line of lines) {
        if (!line.targetCrateDocId) {
          unassigned.push({
            productId: product.productId,
            quantity: line.quantity,
            packageDateUnix: line.packageDateUnix,
          });
        }
      }
    }
    return unassigned;
  }, [analysis, restockPlan]);

  const handleSubmit = async () => {
    // If skip mode, just confirm without any restocking
    if (restockMode === "skip") {
      onConfirm?.();
      return;
    }

    if (!isPlanComplete) {
      message.error(t("unpack_order.assign_all_crates_first"));
      return;
    }

    setSubmitting(true);
    try {
      // Convert restock plan to format expected by executeRestockPlan
      const plan = {};
      for (const [productId, lines] of Object.entries(restockPlan)) {
        plan[productId] = lines.map((line) => ({
          crateDocId: line.targetCrateDocId,
          quantity: line.quantity,
          isNewCrate: line.isNewCrate,
          packageDateUnix: line.packageDateUnix,
          subItemProductId: line.subItemProductId || null,
        }));
      }

      const result = await executeRestockPlan(plan, order, email, uid, products);

      if (result.success) {
        message.success(t("unpack_order.restock_success", { count: result.restocked.length }));
        onConfirm?.();
      } else {
        if (result.failed.length > 0) {
          message.error(
            t("unpack_order.restock_partial_error", {
              failed: result.failed.length,
              success: result.restocked.length,
            }),
          );
        } else {
          message.error(t("unpack_order.restock_failed"));
        }
        // Still call onConfirm since some items may have been restocked
        onConfirm?.();
      }
    } catch (error) {
      console.error("Error executing restock plan:", error);
      message.error(t("unpack_order.restock_error"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRestockModeChange = (mode) => {
    setRestockMode(mode);

    if (mode === "original") {
      // Reset all to original crates (only if available)
      setGlobalFixedCrate(null);
      const newPlan = {};
      for (const product of analysis?.products || []) {
        newPlan[product.productId] = product.crateLines.map((line) => ({
          originalCrateDocId: line.crateDocId,
          targetCrateDocId: line.canRestockToOriginal ? line.crateDocId : null,
          quantity: line.quantity,
          packageDateUnix: line.packageDateUnix,
          isNewCrate: !line.canRestockToOriginal,
          isFixedCrate: false,
          subItemProductId: line.subItemProductId || null,
        }));
      }
      setRestockPlan(newPlan);
    } else if (mode === "fixed") {
      // If there's already a global crate selected, use it; otherwise clear assignments
      if (globalFixedCrate) {
        handleGlobalFixedCrateSelect(globalFixedCrate);
      } else {
        const newPlan = {};
        for (const product of analysis?.products || []) {
          newPlan[product.productId] = product.crateLines.map((line) => ({
            originalCrateDocId: line.crateDocId,
            targetCrateDocId: null,
            quantity: line.quantity,
            packageDateUnix: line.packageDateUnix,
            isNewCrate: true,
            isFixedCrate: true,
            subItemProductId: line.subItemProductId || null,
          }));
        }
        setRestockPlan(newPlan);
      }
    }
  };

  const renderProductSection = (product) => {
    const productName = getProductName(product.productId);
    const lines = restockPlan[product.productId] || [];

    // Simple list for fixed mode
    if (restockMode === "fixed") {
      return (
        <List.Item key={product.productId}>
          <List.Item.Meta
            title={
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Text strong>{`${product.totalQuantity}x ${productName}`}</Text>
              </div>
            }
          />
        </List.Item>
      );
    }

    // Detailed view for original mode
    const dateGroups = groupByPackageDate(lines);

    return (
      <Card key={product.productId} style={{ marginBottom: 16 }} size="small">
        <div style={{ marginBottom: 12, display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
          <Text strong style={{ fontSize: 14 }}>
            {`${product.totalQuantity}x ${productName}`}
          </Text>
        </div>

        {dateGroups.map((group, groupIdx) => (
          <div
            key={groupIdx}
            style={{ marginBottom: 12, paddingInline: 18, paddingBlock: 10, background: "#f5f5f5", borderRadius: 4 }}
          >
            <div>
              {group.lines.map((line, lineIdx) => {
                const originalLine = product.crateLines.find((l) => l.crateDocId === line.originalCrateDocId);
                const isAvailable = originalLine?.canRestockToOriginal;
                const needsRestore = originalLine?.needsRestoreFromLog;

                return (
                  <div
                    key={lineIdx}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 8,
                      padding: "4px 0",
                    }}
                  >
                    <div style={{ width: "100%" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <Text style={{ fontWeight: 500 }}>{line.quantity}x</Text>
                        <Text style={{ wordBreak: "break-word", flex: 1, minWidth: 0 }}>
                          {restockMode === "original" && isAvailable
                            ? needsRestore
                              ? `${t("unpack_order.restore_and_restock")}: ${line.originalCrateDocId}`
                              : `${t("unpack_order.back_to_original")}: ${line.originalCrateDocId}`
                            : t("unpack_order.select_destination")}
                        </Text>
                      </div>
                      <Text type="secondary" style={{ display: "block", wordBreak: "break-word", marginTop: 3 }}>
                        {group.packageDateUnix
                          ? t("unpack_order.packaged_on", {
                              date: dayjs.unix(group.packageDateUnix).format("DD-MM-YYYY"),
                            })
                          : t("unpack_order.unknown_package_date")}
                      </Text>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </Card>
    );
  };

  const unassignedCount = getUnassignedItems.length;

  return (
    <Modal
      title={t("unpack_order.title")}
      open={open}
      onCancel={onCancel}
      width={800}
      style={{ maxWidth: "100%" }}
      styles={{
        body: { maxHeight: "70vh", overflowY: "auto" },
      }}
      footer={
        <Space style={{ width: "100%", justifyContent: "flex-end", flexWrap: "wrap" }}>
          <Button onClick={onCancel} disabled={submitting || loading}>
            {t("cancel")}
          </Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={submitting || loading}
            disabled={!isPlanComplete || analyzing}
          >
            {t("unpack_order.confirm_cancel_restock")}
          </Button>
        </Space>
      }
    >
      {analyzing ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin size="large" />
          <p style={{ marginTop: 16 }}>{t("unpack_order.analyzing_crates")}</p>
        </div>
      ) : !analysis ? (
        <Alert message={t("unpack_order.no_analysis_data")} type="error" />
      ) : analysis.totalItems === 0 ? (
        <div>
          {/*<Alert*/}
          {/*  message={t("unpack_order.no_crate_stock_title")}*/}
          {/*  description={t("unpack_order.no_crate_stock_description")}*/}
          {/*  type="info"*/}
          {/*  showIcon*/}
          {/*/>*/}
          <Paragraph style={{ marginTop: 16 }}>{t("unpack_order.administrative_unpack_info")}</Paragraph>
        </div>
      ) : (
        <div>
          {/*<Alert*/}
          {/*  message={*/}
          {/*    analysis.canRestockAllToOriginal*/}
          {/*      ? t("unpack_order.all_crates_available_title")*/}
          {/*      : t("unpack_order.some_crates_unavailable_title")*/}
          {/*  }*/}
          {/*description=*/}
          {/*{*/}
          {/*  <span style={{ wordBreak: "break-word" }}>*/}
          {/*    {analysis.canRestockAllToOriginal*/}
          {/*      ? t("unpack_order.all_crates_available_desc", { count: analysis.totalItems })*/}
          {/*      : t("unpack_order.some_crates_unavailable_desc", {*/}
          {/*          unavailable: analysis.unavailableCrates.length,*/}
          {/*          total: analysis.originalCrateIds.size,*/}
          {/*        })}*/}
          {/*  </span>*/}
          {/*}*/}
          {/*type={analysis.canRestockAllToOriginal ? "success" : "warning"}*/}
          {/*showIcon style={{ marginBottom: 16 }}*/}
          {/*/>*/}
          {/* Restock Mode Selection */}
          <Card style={{ marginBottom: 16 }}>
            <Title level={5}>{t("unpack_order.select_restock_mode")}</Title>
            <Radio.Group
              value={restockMode}
              onChange={(e) => handleRestockModeChange(e.target.value)}
              disabled={submitting}
              style={{ width: "100%" }}
            >
              <Space direction="vertical" style={{ width: "100%" }}>
                <Radio
                  value="fixed"
                  disabled={fixedDumpingCrates.length === 0}
                  style={{ display: "flex", alignItems: "flex-start" }}
                >
                  <Space style={{ flexWrap: "wrap" }}>
                    <span style={{ wordBreak: "break-word" }}>{t("unpack_order.mode_fixed_dumping")}</span>
                    {fixedDumpingCrates.length === 0 && <Tag size="small">{t("unpack_order.no_fixed_crates")}</Tag>}
                  </Space>
                </Radio>
                <Radio
                  value="original"
                  disabled={!analysis?.canRestockAllToOriginal}
                  style={{ display: "flex", alignItems: "flex-start" }}
                >
                  <Space style={{ flexWrap: "wrap" }}>
                    <span style={{ wordBreak: "break-word" }}>{t("unpack_order.mode_original")}</span>
                    {!analysis?.canRestockAllToOriginal && (
                      <Tag size="small">{t("unpack_order.some_crates_unavailable")}</Tag>
                    )}
                  </Space>
                </Radio>
                <Radio value="skip" style={{ display: "flex", alignItems: "flex-start" }}>
                  <div>
                    <div style={{ wordBreak: "break-word" }}>{t("unpack_order.mode_skip")}</div>
                  </div>
                </Radio>
              </Space>
            </Radio.Group>

            {/*{restockMode === "original" && (*/}
            {/*  <Paragraph type="secondary" style={{ marginTop: 8, wordBreak: "break-word" }}>*/}
            {/*    {t("unpack_order.original_mode_description")}*/}
            {/*  </Paragraph>*/}
            {/*)}*/}
            {restockMode === "fixed" && (
              <>
                {/*<Paragraph type="secondary" style={{ marginTop: 8, wordBreak: "break-word" }}>*/}
                {/*  {t("unpack_order.fixed_mode_description")}*/}
                {/*</Paragraph>*/}
                <div style={{ marginTop: 22 }}>
                  <Text strong style={{ display: "block", marginBottom: 8 }}>
                    {t("unpack_order.select_global_crate")}
                  </Text>
                  <style>{`
                    .unpack-select-no-zoom .ant-select-selector,
                    .unpack-select-no-zoom .ant-select-selection-search-input,
                    .unpack-select-no-zoom input {
                      font-size: 16px !important;
                    }
                  `}</style>
                  <Select
                    placeholder={t("unpack_order.scan_or_select_fixed_crate")}
                    style={{ width: "100%" }}
                    className="unpack-select-no-zoom"
                    value={globalFixedCrate || undefined}
                    onChange={handleGlobalFixedCrateSelect}
                    disabled={submitting || fixedDumpingCrates.length === 0}
                    showSearch
                    filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                  >
                    {fixedDumpingCrates.map((crate) => (
                      <Option key={crate.id || crate.crateId} value={crate.id || crate.crateId}>
                        {t("unpack_order.fixed_crate_option", { crateId: crate.crateId })}
                      </Option>
                    ))}
                  </Select>
                </div>
              </>
            )}
          </Card>
          {/* Products Section - Only show if not in skip mode */}
          {restockMode !== "skip" && (
            <>
              <Title level={5}>
                {restockMode === "fixed" ? t("unpack_order.products_to_dump") : t("unpack_order.products_to_restock")}
              </Title>
              {restockMode !== "fixed" && (
                <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
                  {t("unpack_order.grouped_by_package_date")}
                </Text>
              )}
              {restockMode === "fixed" ? (
                <List
                  size="small"
                  bordered
                  dataSource={analysis.products}
                  renderItem={(product) => renderProductSection(product)}
                  style={{ marginBottom: 16 }}
                />
              ) : (
                analysis.products.map((product) => renderProductSection(product))
              )}
            </>
          )}
          {/* Unavailable Crates Warning */}
          {analysis.unavailableCrates.length > 0 && restockMode !== "original" && (
            <Collapse style={{ marginTop: 16 }}>
              <Panel header={t("unpack_order.unavailable_crates_details")} key="1">
                <List
                  size="small"
                  dataSource={analysis.unavailableCrates}
                  renderItem={(item) => (
                    <List.Item>
                      <List.Item.Meta
                        title={`${getProductName(item.productId)} - ${item.crateDocId}`}
                        description={`${item.quantity} items - ${item.reason}`}
                      />
                      <CloseCircleOutlined style={{ color: "#ff4d4f" }} />
                    </List.Item>
                  )}
                />
              </Panel>
            </Collapse>
          )}
        </div>
      )}
    </Modal>
  );
};

export default UnpackOrderModal;
